import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type {
    StickerDesign,
    Board,
    StickerDesignId,
    ApiResponseStickerDesign,
} from '../utils/Primitives';
import { MutableRefObject } from 'react';
import {
    getLatestStickerDesigns,
    getStickerUrl,
    getMostSlappedStickerDesigns,
    writeStickerDesignToBoard,
} from './StickerDesigns';
import CreateStickerWhileSlapping from './CreateStickerWhileSlapping';
import AddressDisplay from '../components/AddressDisplay';
import { formatEther } from 'viem';
import { getPaymentMethod } from '../payments/PaymentMethods';

const truncateName = (name: string, maxLength: number = 140) => {
    return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
};

const formatStickerPrice = (sticker: StickerDesign, boardRef: MutableRefObject<Board>) => {
    const priceValue = BigInt(sticker.price);
    if (priceValue === BigInt(0)) {
        return null;
    }
    const paymentMethod = getPaymentMethod({ boardRef, paymentMethodId: sticker.paymentMethodId });
    const symbol = paymentMethod?.symbol || 'ETH';
    const price = formatEther(priceValue);
    return `${price} ${symbol}`;
};

const INITIAL_RECENT_LIMIT = 50;
const LOAD_MORE_BATCH_SIZE = 25;

const getCanonicalStickerId = (sticker: StickerDesign): number | null => {
    const parsed = Number(sticker.id);
    return Number.isFinite(parsed) ? parsed : null;
};

const mergeAndSortStickerDesigns = (groups: StickerDesign[][]): StickerDesign[] => {
    const deduped = new Map<number, StickerDesign>();
    groups.flat().forEach((sticker) => {
        const canonicalId = getCanonicalStickerId(sticker);
        if (canonicalId === null) {
            return;
        }
        deduped.set(canonicalId, {
            ...sticker,
            id: canonicalId,
        });
    });
    return Array.from(deduped.values()).sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );
};

const parseTimestampMs = (raw: unknown): number | null => {
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return raw;
    }
    if (typeof raw === 'string') {
        const numeric = Number(raw);
        if (Number.isFinite(numeric)) {
            return numeric;
        }
        const parsedDate = new Date(raw).getTime();
        if (Number.isFinite(parsedDate)) {
            return parsedDate;
        }
    }
    return null;
};

const mapApiStickerToStickerDesign = (
    raw: ApiResponseStickerDesign
): StickerDesign | null => {
    if (!raw?.metadataLoaded) {
        return null;
    }
    const parsedId = Number(raw.stickerId);
    if (!Number.isFinite(parsedId)) {
        return null;
    }
    const publishedAt = parseTimestampMs(raw.publishedAt);
    const paymentMethodId = Number(raw.paymentMethodId);
    const price = Number(raw.price);
    const limit = raw.limit === null ? null : Number(raw.limit);
    const parsedEndTime = parseTimestampMs(raw.endTime);
    const endTime = raw.endTime === null ? null : parsedEndTime;
    const aspectRatio = Number(raw.aspectRatio);
    const slapCount = Number(raw.slapCount);
    if (
        publishedAt === null ||
        !Number.isFinite(paymentMethodId) ||
        !Number.isFinite(price) ||
        !Number.isFinite(aspectRatio)
    ) {
        return null;
    }
    return {
        id: parsedId,
        name: raw.name,
        originalPublisher: raw.originalPublisher,
        originalPublisherEns: raw.originalPublisherEns,
        currentPublisher: raw.currentPublisher,
        currentPublisherEns: raw.currentPublisherEns,
        payoutAddress: raw.payoutAddress,
        publishedAt,
        price,
        paymentMethodId,
        limit,
        endTime,
        aspectRatio,
        imageCID: raw.imageCID,
        metaCID: raw.metadataCID,
        filename: raw.filename,
        description: raw.description,
        url: raw.url,
        slapCount: Number.isFinite(slapCount) ? slapCount : 0,
    } as StickerDesign;
};

interface StickerBrowserProps {
    handleDidClose: () => void;
    boardRef: MutableRefObject<Board>;
    handleStartPublishing: () => void;
    previewStickerId: StickerDesignId | null;
    setPreviewStickerId: (stickerId: StickerDesignId) => void;
}

export default function StickerBrowser(props: StickerBrowserProps) {
    const { handleDidClose, boardRef, handleStartPublishing } = props;
    const latestStickerDesigns = getLatestStickerDesigns({
        boardRef,
        count: INITIAL_RECENT_LIMIT,
    });
    const mostSlappedStickerDesigns = getMostSlappedStickerDesigns({ boardRef, count: 5 });
    const [recentStickerDesigns, setRecentStickerDesigns] = useState<StickerDesign[]>(
        latestStickerDesigns
    );
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMoreRecent, setHasMoreRecent] = useState(
        latestStickerDesigns.length >= INITIAL_RECENT_LIMIT
    );
    const [nextOffset, setNextOffset] = useState(latestStickerDesigns.length);
    const [hasRequestedMore, setHasRequestedMore] = useState(false);
    const stickercloudApiBase = useMemo(() => {
        const raw = process.env.NEXT_PUBLIC_STICKERCLOUD_API as string | undefined;
        return raw ? raw.replace(/\/$/, '') : null;
    }, []);

    const handleDidClickSticker = (sticker: StickerDesign) => {
        props.setPreviewStickerId(sticker.id);
    }

    const handleLoadMoreRecent = useCallback(async () => {
        if (!stickercloudApiBase || isLoadingMore || !hasMoreRecent) {
            return;
        }
        setIsLoadingMore(true);
        setHasRequestedMore(true);
        try {
            const params = new URLSearchParams({
                offset: String(nextOffset),
                limit: String(LOAD_MORE_BATCH_SIZE),
            });
            console.log('[StickerBrowser] LoadMore request', {
                offset: nextOffset,
                limit: LOAD_MORE_BATCH_SIZE,
            });
            const response = await fetch(
                `${stickercloudApiBase}/sticker-designs?${params.toString()}`
            );
            if (!response.ok) {
                throw new Error(`Failed to load more stickers (${response.status})`);
            }
            const json = await response.json();
            const apiStickers = Array.isArray(json?.data) ? json.data : [];
            const loaded = (apiStickers as ApiResponseStickerDesign[])
                .map(mapApiStickerToStickerDesign)
                .filter((item): item is StickerDesign => item !== null);
            console.log('[StickerBrowser] LoadMore response', {
                apiCount: apiStickers.length,
                loadedCount: loaded.length,
                pagination: json?.pagination ?? null,
            });

            loaded.forEach((sticker) => {
                writeStickerDesignToBoard({ boardRef, sticker });
            });

            const merged = mergeAndSortStickerDesigns([recentStickerDesigns, loaded]);
            setRecentStickerDesigns((prev) => {
                const isSame =
                    merged.length === prev.length &&
                    merged.every((item, idx) => prev[idx]?.id === item.id);
                console.log('[StickerBrowser] LoadMore merged', {
                    previousCount: prev.length,
                    mergedCount: merged.length,
                });
                return isSame ? prev : merged;
            });

            if (typeof json?.pagination?.nextOffset === 'number') {
                setNextOffset(json.pagination.nextOffset);
            } else {
                setNextOffset((prev) => prev + loaded.length);
            }
            if (typeof json?.pagination?.hasMore === 'boolean') {
                setHasMoreRecent(json.pagination.hasMore);
            } else {
                const hasNewItems = merged.length > recentStickerDesigns.length;
                setHasMoreRecent(hasNewItems && loaded.length >= LOAD_MORE_BATCH_SIZE);
            }
        } catch (error) {
            console.error('Failed to load more recent stickers', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [
        stickercloudApiBase,
        isLoadingMore,
        hasMoreRecent,
        nextOffset,
        recentStickerDesigns,
        boardRef,
    ]);

    useEffect(() => {
        if (latestStickerDesigns.length === 0) {
            return;
        }
        if (!hasRequestedMore && latestStickerDesigns.length >= INITIAL_RECENT_LIMIT) {
            setHasMoreRecent(true);
        }
        setRecentStickerDesigns((prev) => {
            const merged = mergeAndSortStickerDesigns([prev, latestStickerDesigns]);
            const isSame =
                merged.length === prev.length &&
                merged.every((item, idx) => prev[idx]?.id === item.id);
            return isSame ? prev : merged;
        });
    }, [latestStickerDesigns, hasRequestedMore]);

    return (
        <div className="relative flex flex-col items-center justify-center px-2 py-4 md:p-6 shadow-lg w-full">
            <div className="max-w-screen-xl w-full flex flex-col items-center justify-center">
                <div className='text-base md:text-xl md:leading-loose text-center px-0 mt-20 mb-0 md:mt-8 md:mb-2 '>
                    Pick A Sticker For Your Next Slap<br />
                </div>

                <div className="text-lg mt-8 mb-5 font-bold">Most Slapped:</div>
                <div className='flex flex-wrap justify-center gap-x-2 gap-y-3 px-2 md:gap-x-4 md:gap-y-6'>
                    {mostSlappedStickerDesigns.map((sticker, index) =>
                        sticker ? (
                            <div key={`most-slapped-${sticker.id}`}
                                className='basis-[calc(33.333%-0.5rem)] max-w-[calc(33.333%-0.5rem)] p-2 md:basis-auto md:max-w-none md:p-2 bg-gray-200 border-2 border-gray-300 rounded hover:cursor-pointer hover:shadow-lg hover:scale-105 transition-transform flex flex-col items-center min-w-0'
                                onClick={() => { handleDidClickSticker(sticker) }}>
                                <div className='pb-1 text-xs md:text-base text-center text-slate-900'>#{index + 1}</div>
                                <img src={getStickerUrl(sticker)} alt={sticker.name} className="w-20 h-20 md:w-24 md:h-24 object-contain" />
                                <div className='sticker-name-wrap pt-1 md:pt-1 text-[11px] md:text-xs text-center w-20 md:w-28 text-slate-900 h-[44px] md:h-[60px] overflow-hidden md:line-clamp-4'>
                                    {truncateName(sticker.name, 70)}
                                </div>
                                <div className='pt-1 text-[11px] md:text-xs text-center w-20 md:w-28 text-slate-700 whitespace-nowrap h-4 md:h-5'>
                                    {formatStickerPrice(sticker, boardRef) || '\u00A0'}
                                </div>
                            </div>
                        ) : null
                    )}
                </div>

                <CreateStickerWhileSlapping handleStartPublishing={handleStartPublishing} theIdentifier={'-12323'} />

                <div className="flex flex-col overflow-y-auto p-2 items-center justify-center">
                    <div className="text-lg mt-8 mb-5 font-bold">Recently Published:</div>
                    <div className='flex flex-wrap justify-center gap-x-2 gap-y-6 px-2 max-w-[800px] md:gap-x-4 md:gap-y-10'>
                        {recentStickerDesigns.map((sticker, index) =>
                            sticker ? (
                            <div key={`recent-publish-${sticker.id}`}
                                    className='basis-[calc(33.333%-0.5rem)] max-w-[calc(33.333%-0.5rem)] p-2 md:basis-auto md:max-w-none md:p-2 bg-gray-200 border-2 border-gray-300 rounded hover:cursor-pointer hover:shadow-lg hover:scale-105 transition-transform flex flex-col items-center min-w-0'
                                    onClick={() => { handleDidClickSticker(sticker) }}>
                                    <div className='text-xs text-center mb-2 text-slate-900 h-5'>
                                        <AddressDisplay address={sticker.currentPublisher} ensName={sticker.currentPublisherEns} />
                                    </div>
                                    <img src={getStickerUrl(sticker)} alt={sticker.name} className="w-20 h-20 md:w-24 md:h-24 object-contain" />
                                    <div className='sticker-name-wrap pt-1 md:pt-1 text-[11px] md:text-xs text-center w-20 md:w-28 text-slate-900 h-[44px] md:h-[60px] overflow-hidden md:line-clamp-4'>
                                        {truncateName(sticker.name)}
                                    </div>
                                    <div className='pt-1 text-[11px] md:text-xs text-center w-20 md:w-28 text-slate-700 whitespace-nowrap h-4 md:h-5'>
                                        {formatStickerPrice(sticker, boardRef) || '\u00A0'}
                                    </div>
                                </div>
                            ) : null
                        )}
                    </div>
                    {stickercloudApiBase && hasMoreRecent && (
                        <div className="mt-6">
                            <button
                                onClick={handleLoadMoreRecent}
                                disabled={isLoadingMore}
                                className="px-5 py-2 rounded-md bg-slate-200 hover:bg-slate-300 disabled:opacity-60 disabled:cursor-not-allowed text-slate-800 font-semibold"
                            >
                                {isLoadingMore ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className='fixed top-2 right-2'>
                <button
                    onClick={handleDidClose}
                    className="text-slate-800 m-4 py-1 px-3 text-2xl bg-slate-100 hover:bg-slate-300 rounded-full"
                >
                    X
                </button>
            </div>
        </div>
    );
}
